import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { captureException, setUser as setSentryUser } from "@sentry/nextjs";
import Cookies from "js-cookie";
import shallow from "zustand/shallow";
import useUserStore from "stores/use-user-store";
import { useHeap } from "hooks/use-heap";
import { useIntercom } from "hooks/use-intercom";
import { MS_IN_S, S_IN_M } from "utils/date";
import { postAuthRefresh } from "./utils";

const FOUR_MINUTES = 4 * S_IN_M * MS_IN_S;

/**
 * Refresh the auth token every four minutes, or if the token is gone sign the user out
 */
const useIntervalRefresh = () => {
  const { user } = useUserStore(
    (store) => ({
      user: store.user,
    }),
    shallow
  );

  const { updateIntercom } = useIntercom();
  const { updateHeap } = useHeap();

  const router = useRouter();
  const { asPath } = router;

  const refreshTimer = useRef<NodeJS.Timer>();

  useEffect(() => {
    if (router.isReady && !user.loading && user.isLoggedIn) {
      if (user.details?.intercom && user.details?.username) {
        updateIntercom({
          id: user.details.id,
          name: user.details.username,
          email: user.details.username,
          group: user.access.group,
          intercom: user.details.intercom,
        });
        updateHeap({ ...user.details, group: user.access.group });
        setSentryUser({
          email: user.details.username,
          ...user,
        });
      }

      const refreshOrLogout = async () => {
        try {
          if (!Cookies.get("payload")) {
            throw Error("No payload cookie for postAuthRefresh");
          }
          await postAuthRefresh();
        } catch (error) {
          captureException(error);
          if (!user.loading) {
            router.push(`/signin?redirect=${asPath}`);
          }
        }
      };

      refreshTimer.current = setInterval(refreshOrLogout, FOUR_MINUTES);

      return () => clearInterval(refreshTimer.current);
    }

    return () => {};
  }, [asPath, router, updateHeap, updateIntercom, user]);
};

export default useIntervalRefresh;
